/**
 * Typed API client + endpoint metadata helpers.
 *
 * We use `openapi-typescript` to generate `types.gen.ts`, then expose:
 *
 *   - `getEndpoints()` — flat list of all endpoints with metadata for UI
 *   - `getEndpoint(operationId)` — single endpoint by operationId
 *   - `request(operationId, options)` — typed fetcher that auto-attaches the
 *     `bo_session` cookie (via `credentials: 'include'`) and resolves to the
 *     OpenAPI-declared response shape when possible.
 *
 * The full OpenAPI document itself is loaded at runtime (small enough at ~300
 * paths to fetch in one go) so we have both the static TS types AND the runtime
 * schema needed to render forms / cURL / response examples.
 */

import openapiSpec from '../../../openapi/output/openapi.json';
import type { paths } from './types.gen';

// ── Public types ─────────────────────────────────────────────────────────

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete' | 'head' | 'options';

export interface EndpointInfo {
  /** Full path with `{param}` placeholders, e.g., `/api/chats/{id}`. */
  path: string;
  method: HttpMethod;
  /** Uppercase for display: GET, POST, ... */
  methodUpper: string;
  operationId: string;
  summary: string;
  description: string;
  tags: string[];
  /** Primary tag for sidebar grouping. */
  tag: string;
  /** Raw OpenAPI operation object — full shape from spec. */
  operation: OpenApiOperation;
}

export interface OpenApiOperation {
  tags?: string[];
  summary?: string;
  description?: string;
  operationId?: string;
  parameters?: OpenApiParameter[];
  requestBody?: {
    description?: string;
    required?: boolean;
    content?: Record<string, { schema?: OpenApiSchema }>;
  };
  responses?: Record<string, { description?: string; content?: Record<string, { schema?: OpenApiSchema }> }>;
  'x-source-file'?: string;
}

export interface OpenApiParameter {
  name: string;
  in: 'path' | 'query' | 'header' | 'cookie';
  required?: boolean;
  description?: string;
  schema?: OpenApiSchema;
}

export interface OpenApiSchema {
  type?: string;
  format?: string;
  enum?: unknown[];
  items?: OpenApiSchema;
  properties?: Record<string, OpenApiSchema>;
  required?: string[];
  description?: string;
  example?: unknown;
}

// ── Spec parsing ─────────────────────────────────────────────────────────

const HTTP_METHODS: HttpMethod[] = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options'];

let _endpoints: EndpointInfo[] | null = null;

export function getEndpoints(): EndpointInfo[] {
  if (_endpoints) return _endpoints;

  const list: EndpointInfo[] = [];
  // openapiSpec.paths is loosely typed — runtime values are the operations.
  const spec = openapiSpec as unknown as {
    paths: Record<string, Record<string, OpenApiOperation>>;
  };

  for (const [path, methods] of Object.entries(spec.paths)) {
    for (const method of HTTP_METHODS) {
      const op = methods[method];
      if (!op) continue;
      const tag = op.tags?.[0] || 'Other';
      list.push({
        path,
        method,
        methodUpper: method.toUpperCase(),
        operationId: op.operationId || `${method}_${path}`,
        summary: op.summary || `${method.toUpperCase()} ${path}`,
        description: op.description || '',
        tags: op.tags || [tag],
        tag,
        operation: op,
      });
    }
  }

  // Sort: by tag, then path
  list.sort((a, b) => {
    const tagCmp = a.tag.localeCompare(b.tag);
    if (tagCmp !== 0) return tagCmp;
    return a.path.localeCompare(b.path);
  });

  _endpoints = list;
  return list;
}

export function getEndpoint(operationId: string): EndpointInfo | null {
  return getEndpoints().find((e) => e.operationId === operationId) ?? null;
}

/**
 * Find endpoint by path + method (case-insensitive).
 * Useful when routing from URL params like `/endpoints/{tag}/{slug}`.
 */
export function getEndpointByPathMethod(path: string, method: string): EndpointInfo | null {
  const m = method.toLowerCase() as HttpMethod;
  return getEndpoints().find((e) => e.path === path && e.method === m) ?? null;
}

export function getTags(): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const ep of getEndpoints()) {
    counts.set(ep.tag, (counts.get(ep.tag) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ── Typed request helper ─────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_PUBLIC_URL as string | undefined) || '';

export interface RequestOptions {
  path?: Record<string, string | number>;
  query?: Record<string, unknown>;
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
}

export interface RequestResult<T = unknown> {
  status: number;
  statusText: string;
  ok: boolean;
  headers: Record<string, string>;
  data: T | null;
  /** Time-to-response in ms. */
  durationMs: number;
}

/**
 * Fire a typed request against the playground backend.
 *
 * The `bo_session` cookie is auto-included via `credentials: 'include'`. Pass
 * an explicit `headers.Authorization` to override.
 *
 * Returns the response wrapped in metadata (status, timing, raw headers) so
 * the playground UI can render the full picture, not just the body.
 */
export async function fireRequest(
  endpoint: EndpointInfo,
  options: RequestOptions = {}
): Promise<RequestResult> {
  const url = buildUrl(endpoint.path, options.path, options.query);
  const init: RequestInit = {
    method: endpoint.methodUpper,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
    signal: options.signal,
  };
  if (options.body !== undefined && !['GET', 'HEAD'].includes(init.method!)) {
    init.body = JSON.stringify(options.body);
  }

  const start = performance.now();
  const res = await fetch(url, init);
  const durationMs = performance.now() - start;

  const headers: Record<string, string> = {};
  res.headers.forEach((value, key) => {
    headers[key] = value;
  });

  let data: unknown = null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else if (contentType.startsWith('text/')) {
    data = await res.text();
  }

  return {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    headers,
    data,
    durationMs,
  };
}

function buildUrl(
  pathPattern: string,
  pathParams?: Record<string, string | number>,
  queryParams?: Record<string, unknown>
): string {
  // Substitute path params
  let resolved = pathPattern;
  if (pathParams) {
    for (const [k, v] of Object.entries(pathParams)) {
      resolved = resolved.replace(`{${k}}`, encodeURIComponent(String(v)));
    }
  }
  // Append query params
  const usp = new URLSearchParams();
  if (queryParams) {
    for (const [k, v] of Object.entries(queryParams)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) {
        for (const item of v) usp.append(k, String(item));
      } else {
        usp.set(k, String(v));
      }
    }
  }
  const qs = usp.toString();
  const base = API_BASE || ''; // empty = same-origin (Vite proxies /api in dev)
  return `${base}${resolved}${qs ? `?${qs}` : ''}`;
}

// Re-export the generated types for consumers who want compile-time safety.
export type { paths };
